import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, type FC, type PropsWithChildren } from "react";
import { debounce } from "throttle-debounce";
import { AppForm } from "@/shared/Components";
import { useCreatePersonMutation, useUpdatePersonMutation, type Person } from "@/services/api";
import { useAppDispatch } from "@/services/store";
import {
  setFormIsValid,
  setCurrentPerson,
  setFormIsValidating,
  setUpdateIsLoading,
} from "@/pages/ContactsPage/slice";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type {
  CreatePersonRequest,
  UpdatePersonRequest,
} from "@/services/api/entities/person/types";
import type { GeneralInfoTabFormValues } from "../../schema";
import { getGeneralInfoTabSchema } from "../../schema";
import {
  getDefaultGeneralTabInfoFormValues,
  transformGeneralInfoFormDataToCreatePerson,
  transformGeneralInfoFormDataToUpdatePerson,
} from "../../helpers";

type Props = {
  defaultValuesPerson: Person;
};

export const GeneralInfoTabForm: FC<PropsWithChildren<Props>> = ({
  children,
  defaultValuesPerson,
}) => {
  const dispatch = useAppDispatch();
  const [updatePerson] = useUpdatePersonMutation();
  const [createPerson] = useCreatePersonMutation();

  const { callError } = useAlert();

  const defaultValues = useMemo(() => {
    return getDefaultGeneralTabInfoFormValues(defaultValuesPerson);
  }, [defaultValuesPerson]);

  const formApi = useForm<GeneralInfoTabFormValues>({
    resolver: yupResolver(getGeneralInfoTabSchema()),
    mode: "all",
    values: defaultValues,
    shouldFocusError: false,
  });

  const upsertPerson = useCallback(
    async (data: GeneralInfoTabFormValues) => {
      const isTempPerson = defaultValuesPerson?.id.includes("new-");

      const transformedData = isTempPerson
        ? transformGeneralInfoFormDataToCreatePerson({ data, defaultValuesPerson })
        : transformGeneralInfoFormDataToUpdatePerson({ data, defaultValuesPerson });

      if (isTempPerson) {
        const res = await createPerson(transformedData as CreatePersonRequest)
          .unwrap()
          .catch((e) => {
            callError({ text: getErrorMessage(e.code) });
          });

        if (res) {
          dispatch(setUpdateIsLoading(false));
          dispatch(setCurrentPerson(res));
        } else {
          dispatch(setUpdateIsLoading(false));
        }
      } else {
        const res = await updatePerson(transformedData as UpdatePersonRequest)
          .unwrap()
          .catch((e) => {
            callError({ text: getErrorMessage(e.code) });
          });

        if (res) {
          dispatch(setUpdateIsLoading(false));
          dispatch(setCurrentPerson(res));
        } else {
          dispatch(setUpdateIsLoading(false));
        }
      }
    },
    [createPerson, defaultValuesPerson, dispatch, updatePerson],
  );

  const submit = useMemo(
    () =>
      debounce(4000, async (data: GeneralInfoTabFormValues) => {
        const isValid = await formApi.trigger();
        if (isValid) {
          upsertPerson(data);
        }
      }),
    [formApi, upsertPerson],
  );

  const triggerHandler = () => {
    dispatch(setFormIsValidating(true));

    return formApi.trigger().then((valid) => {
      dispatch(setFormIsValidating(false));
      dispatch(setFormIsValid(valid));
      return valid;
    });
  };

  useEffect(() => {
    triggerHandler();
  }, []);

  useEffect(() => {
    formApi.reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues]);

  useEffect(() => {
    const subscription = formApi.watch(async (values) => {
      const isValid = await triggerHandler();

      if (isValid && formApi.formState.isDirty) {
        dispatch(setUpdateIsLoading(true));

        submit(values as GeneralInfoTabFormValues);
      }

      if (!isValid) {
        dispatch(setUpdateIsLoading(false));
      }
    });

    return () => subscription.unsubscribe();
  }, [formApi.watch, submit]);

  return <AppForm formApi={formApi}>{children}</AppForm>;
};
